/*
 * @Author: Ryan
 * @Date: 2025-12-29
 * @Description: Testimonials section - Trusted by 100,000+ Teams Worldwide
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'A Complete Game-Changer for Podcasters',
    content:
      "With FalcoCut, I no longer spend hours re-recording intros or corrections. The cloned voice sounds just like me, and my audience can't tell the difference. It saves me time and keeps my podcast consistent.",
    role: 'Podcast Producer',
    name: 'Michael Chen',
  },
  {
    title: 'High-Quality Ads Without the Studio',
    content:
      "I use FalcoCut for multilingual ad campaigns, and it's been a game-changer. Instead of hiring multiple voice actors for each market, I can clone my voice and instantly generate versions in different languages.",
    role: 'Marketing Strategist',
    name: 'Hannah Kim',
  },
  {
    title: 'Reaching a Global Audience in My Own Voice',
    content:
      "I now produce content in English, Spanish, and Japanese—all in my own cloned voice. Followers say it feels personal, like I'm talking to them directly. It's the easiest way to grow globally.",
    role: 'Content Creator',
    name: 'Emma Rodriguez',
  },
  {
    title: 'Turning Books Into Audio Effortlessly',
    content:
      'Instead of weeks in the studio, I cloned my voice once and now narrate entire audiobooks in minutes. The AI captures my tone and pacing perfectly, and listeners love the natural flow.',
    role: 'Audiobook Author',
    name: 'David Wilson',
  },
  {
    title: 'Teaching Online Has Never Been Easier',
    content:
      'I use my cloned voice to generate lessons and tutorials without endless recording. Students stay engaged because it still feels like me, and I save hours every week.',
    role: 'Educator',
    name: 'Olivia Parker',
  },
  {
    title: 'Expanding My Channel Internationally',
    content:
      "Now I can release videos in multiple languages using my own cloned voice. Fans love hearing me directly, even if they don't speak my native language.",
    role: 'YouTube Creator',
    name: 'Diego Morales',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Users Say About Our AI Voice Cloning
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
